import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TourEvent } from '@/features/events';
import { formatDateRangeShort } from '@/features/events';
import { ArchFrame } from '@/components/ornaments/ArchFrame';
import { VipCrown } from '@/components/ornaments/VipCrown';
import { cx } from '@/lib/cx';
import styles from './DestinationCard.module.css';

export interface DestinationCardProps {
  event: TourEvent;
  /**
   * Imagen del landmark. La inyecta la pagina desde el manifiesto de
   * FORGE-BRAVO; el substrato no conoce rutas de assets.
   */
  media?: ReactNode;
  /** Numero de orden dentro de la ruta (el "5" de la tarjeta de Cancun). */
  showOrder?: boolean;
  className?: string;
}

/**
 * Tarjeta de destino del hub: arco superior con imagen, bandera, ciudad, fecha
 * y distintivo VIP. Toda ella es un enlace real a la landing del destino.
 *
 * Los datos salen SIEMPRE del `event` tipado. Ninguna pagina teclea "23-24 ENE".
 */
export function DestinationCard({ event, media, showOrder = false, className }: DestinationCardProps) {
  const titleId = `destino-${event.slug}`;

  return (
    <article className={cx(styles.card, event.isVip && styles.vip, className)} aria-labelledby={titleId}>
      {event.isVip ? (
        <>
          <span className={styles.crown}>
            <VipCrown size={38} />
          </span>
          {/* LA CINTA VIP ES DIAGONAL Y LA RECORTA EL BORDE DE LA TARJETA, como
              en el poster. El recorte vive en esta capa y NO en `.card` porque
              la corona y la chapa de bandera tienen que poder salirse del
              contorno: un `overflow: hidden` en la tarjeta se las comeria.
              El envoltorio es presentacional puro (un `span` sin rol), asi que
              "VIP" sigue siendo texto real en el arbol de accesibilidad. */}
          <span className={styles.ribbonSlot}>
            <span className={styles.badge}>VIP</span>
          </span>
        </>
      ) : null}

      {showOrder ? (
        <span className={styles.order} aria-hidden="true">
          {event.order}
        </span>
      ) : null}

      {/* HUECO MENOS ALTO QUE EL 3/4 POR DEFECTO, y va por prop a proposito:
          `ArchFrame` escribe el `aspect-ratio` en el `style` del elemento, asi que
          ninguna regla CSS de aqui puede ganarle sin `!important`.
          El poster da tarjetas de 174x250 con el arco comiendose ~70 % del alto;
          con 3/4 la tarjeta se iba a 350 px y la seccion al doble que el poster. */}
      <ArchFrame className={styles.media} bordered={false} ratio="5 / 6">
        {media ?? <span className={styles.mediaFallback} aria-hidden="true" />}
      </ArchFrame>

      <div className={styles.body}>
        <span className={styles.flag} aria-hidden="true">
          {event.flag}
        </span>
        <h3 className={styles.city} id={titleId}>
          <Link className={styles.link} to={event.path}>
            {/* Rotulo CORTO: es el que usa el mockup y el unico que entra en una
                linea. El nombre completo va en el texto accesible del enlace. */}
            {event.shortName}
            {/* El nombre accesible del enlace dice a donde va, no "leer mas". */}
            <span className="az-visually-hidden">
              {' '}
              — ver el destino de {event.city}, {formatDateRangeShort(event)}
            </span>
          </Link>
        </h3>
        <p className={styles.dates}>
          <time dateTime={event.startDate}>{formatDateRangeShort(event)}</time>
        </p>

        {/* PISTA DE QUE LA TARJETA SE PULSA. La tarjeta entera ya era pulsable
            —`.link::after` con `inset: 0` cubre tambien la foto— pero nada lo
            decia: el cliente reporto (31-jul-2026) que la gente no intuye que
            hay que tocar la imagen. Un area pulsable invisible es un area
            pulsable que no existe.

            Va en `aria-hidden` A PROPOSITO: el enlace ya se anuncia como «BOGOTA
            — ver el destino de Bogota, 30-31 ENE». Leer ademas «Ver destino»
            duplicaria la accion en el lector de pantalla; esto es la version
            VISUAL de un nombre accesible que ya existe.

            No lleva `<a>` propio: dos enlaces al mismo sitio dentro de una misma
            tarjeta ensucian la navegacion por tabulador y el conteo de enlaces. */}
        <span className={styles.cue} aria-hidden="true">
          Ver destino
          <svg
            className={styles.cueArrow}
            viewBox="0 0 16 16"
            width="11"
            height="11"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            focusable="false"
          >
            <path d="M3 8h9M8.6 4.6 12 8l-3.4 3.4" />
          </svg>
        </span>
      </div>
    </article>
  );
}
